import React from 'react';
import { Timestamp } from 'firebase/firestore';
import {
  EnvelopeIcon,
  PhoneIcon,
  HomeIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/solid';
import { ProfileHeaderProps } from './types';
import { Landlord, Renter, PropertyAgent } from '../../../types/userTypes';
import { capitalizeWords } from '../../../utility/capitalizeWords';
import ProfilePhoto from './ProfilePhoto';

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  profile,
  currentUser
}) => {
  return (
    <div className="flex flex-col md:flex-row md:items-start md:space-x-8">
      {/* Profile Photo and Name */}
      <div className="flex flex-col items-center md:items-start text-center md:text-left mb-6 md:mb-0">
        <ProfilePhoto
          photoUrl={profile.photoUrl || currentUser?.photoURL || ''}
          userName={profile.name}
          isEditing={false}
        />
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-1">
          {capitalizeWords(profile.name)}
        </h2>
        <p className="text-gray-500 text-sm">
          Member since{' '}
          {profile.accountCreationDate instanceof Timestamp
            ? profile.accountCreationDate.toDate().toLocaleDateString()
            : 'N/A'}
        </p>
      </div>

      {/* Contact Information and Bio */}
      <div className="flex-grow">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-gray-600 mb-1 text-sm">Email</label>
            <p className="flex items-center text-gray-700 text-sm md:text-base">
              <EnvelopeIcon className="w-5 h-5 mr-2 text-gray-400" />
              {profile.email}
            </p>
          </div>
          <div>
            <label className="block text-gray-600 mb-1 text-sm">Phone</label>
            <p className="flex items-center text-gray-700 text-sm md:text-base">
              <PhoneIcon className="w-5 h-5 mr-2 text-gray-400" />
              {profile.phone}
            </p>
          </div>

          {/* Company field for landlords */}
          {profile.role === 'landlord' && (
            <div>
              <label className="block text-gray-600 mb-1 text-sm">
                Company
              </label>
              <p className="flex items-center text-gray-700 text-sm md:text-base">
                <BuildingOfficeIcon className="w-5 h-5 mr-2 text-gray-400" />
                {(profile as Landlord).company}
              </p>
            </div>
          )}

          {/* PSRA Licence for agents */}
          {'psraLicence' in profile && (
            <div>
              <label className="block text-gray-600 mb-1 text-sm">
                PSRA Licence
              </label>
              <p className="flex items-center text-gray-700 text-sm md:text-base">
                <BuildingOfficeIcon className="w-5 h-5 mr-2 text-gray-400" />
                {(profile as PropertyAgent).psraLicence}
              </p>
            </div>
          )}

          {/* Preferred Location for renters */}
          {profile.role === 'renter' && (
            <div>
              <label className="block text-gray-600 mb-1 text-sm">
                Preferred Location
              </label>
              <p className="flex items-center text-gray-700 text-sm md:text-base">
                <HomeIcon className="w-5 h-5 mr-2 text-gray-400" />
                {(profile as Renter).preferredLocation}
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="block text-gray-600 mb-1 text-sm">Bio</label>
          <p className="text-gray-700 leading-relaxed text-sm md:text-base">
            {profile.bio || 'N/A'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
